<template>
  <form class="filters" @submit.prevent="apply">
    <slot />
    <div v-if="showToggles && toggles.length" class="filters__toggles">
      <label v-for="toggle in toggles" :key="toggle.key" class="filters__checkbox">
        <input type="checkbox" v-model="local[toggle.key]" />
        <span class="filters__checkbox-box"></span>
        <span class="filters__checkbox-text">{{ toggle.label }}</span>
      </label>
    </div>

    <!-- Actions -->
    <div class="filters__actions">
      <button type="button" class="filters__btn filters__btn--ghost" @click="reset">Reset</button>
      <button type="submit" class="filters__btn filters__btn--primary">Apply filters</button>
    </div>
  </form>
</template>

<script>
export default {
  name: 'FiltersComponent',

  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },
    showToggles: {
      type: Boolean,
      default: false,
    },
    toggles: {
      type: Array,
      default: () => [],
    },
    defaultFilters: {
      type: Object,
      default: null,
    },
  },

  emits: ['update:modelValue', 'apply', 'reset'],

  data() {
    return {
      local: { ...this.modelValue },
      initialSnapshot: { ...this.modelValue },
    }
  },

  watch: {
    // Keep local draft in sync if the parent resets modelValue externally.
    modelValue: {
      handler(val) {
        Object.assign(this.local, val)
      },
      deep: true,
    },
  },

  methods: {
    apply() {
      this.$emit('update:modelValue', { ...this.local })
      this.$emit('apply', { ...this.local })
    },

    reset() {
      const target = this.defaultFilters ?? this.initialSnapshot
      Object.keys(this.local).forEach((key) => delete this.local[key])
      Object.assign(this.local, target)
      this.$emit('update:modelValue', { ...this.local })
      this.$emit('reset')
    },
  },
}
</script>

<style scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 20px;
  margin-bottom: 10px;

  background: rgb(242 240 240 / 55%);
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.65);
  box-shadow: 0 8px 30px -12px rgba(93, 28, 106, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
.filters * {
  box-sizing: border-box;
}

:slotted(.filters__search) {
  position: relative;
  flex: 1 1 240px;
  min-width: 200px;
  display: flex;
  align-items: center;
}
:slotted(.filters__icon) {
  position: absolute;
  left: 14px;
  width: 16px;
  height: 16px;
  color: var(--color-text-muted);
  pointer-events: none;
}
:slotted(.filters__search-input) {
  width: 100%;
  height: 42px;
  padding: 0 14px 0 38px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  background: rgba(255, 255, 255, 0.7);
  font-size: 13.5px;
  font-weight: 500;
  color: var(--color-text-dark);
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}
:slotted(.filters__search-input)::placeholder {
  color: var(--color-text-muted);
  font-weight: 400;
}
:slotted(.filters__search-input):focus {
  background: var(--color-surface);
  border-color: var(--color-secondary);
  box-shadow: 0 0 0 3px rgba(202, 89, 149, 0.16);
}

:slotted(.filters__field) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
}
:slotted(.filters__label) {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--color-text-muted);
  padding-left: 2px;
}

:slotted(.filters__select-wrap) {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}
:slotted(.filters__select) {
  appearance: none;
  width: 100%;
  height: 42px;
  padding: 0 34px 0 12px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  background: rgba(255, 255, 255, 0.7);
  font-size: 13.5px;
  font-weight: 500;
  color: var(--color-text-dark);
  cursor: pointer;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}
:slotted(.filters__select):focus {
  background: var(--color-surface);
  border-color: var(--color-secondary);
  box-shadow: 0 0 0 3px rgba(202, 89, 149, 0.16);
}
:slotted(.filters__chevron) {
  position: absolute;
  right: 12px;
  width: 13px;
  height: 13px;
  color: var(--color-text-muted);
  pointer-events: none;
}

:slotted(.filters__field--range) {
  min-width: 220px;
}
:slotted(.filters__range) {
  display: flex;
  align-items: center;
  gap: 6px;
}
:slotted(.filters__date) {
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 0 10px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  background: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-dark);
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
:slotted(.filters__date):focus {
  border-color: var(--color-secondary);
  box-shadow: 0 0 0 3px rgba(202, 89, 149, 0.16);
}
:slotted(.filters__range-sep) {
  color: var(--color-text-muted);
  font-size: 12px;
  flex-shrink: 0;
}

.filters__toggles {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding-bottom: 9px;
}
.filters__checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.filters__checkbox input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.filters__checkbox-box {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1.5px solid var(--color-border);
  background: var(--color-surface);
  display: grid;
  place-items: center;
  flex-shrink: 0;
  transition: background 0.16s ease, border-color 0.16s ease;
}
.filters__checkbox-box::after {
  content: '';
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) scale(0);
  transition: transform 0.16s ease;
  margin-bottom: 2px;
}
.filters__checkbox input:checked + .filters__checkbox-box {
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  border-color: transparent;
}
.filters__checkbox input:checked + .filters__checkbox-box::after {
  transform: rotate(45deg) scale(1);
}
.filters__checkbox-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-dark);
}

.filters__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding-bottom: 1px;
}
.filters__btn {
  height: 42px;
  padding: 0 18px;
  border-radius: 12px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: transform 0.15s ease, box-shadow 0.2s ease, opacity 0.15s ease;
  white-space: nowrap;
}
.filters__btn:active {
  transform: scale(0.97);
}
.filters__btn--ghost {
  background: rgba(255, 255, 255, 0.6);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
}
.filters__btn--ghost:hover {
  color: var(--color-text-dark);
  background: var(--color-surface);
}
.filters__btn--primary {
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(93, 28, 106, 0.45);
}
.filters__btn--primary:hover {
  box-shadow: 0 8px 20px -6px rgba(93, 28, 106, 0.55);
}

/* ==========================================================================
   Responsive
   ========================================================================== */
@media (max-width: 900px) {
  :slotted(.filters__search) {
    flex: 1 1 200px;
  }
  :slotted(.filters__field) {
    flex: 1 1 160px;
    min-width: 140px;
  }
}

@media (max-width: 720px) {
  .filters {
    padding: 16px;
    gap: 12px;
  }
  :slotted(.filters__search),
  :slotted(.filters__field),
  :slotted(.filters__field--range) {
    flex: 1 1 100%;
    min-width: 0;
    width: 100%;
  }
  .filters__actions {
    margin-left: 0;
    width: 100%;
  }
  .filters__btn {
    flex: 1;
  }
}

@media (max-width: 480px) {
  .filters {
    padding: 14px;
    gap: 10px;
    border-radius: 16px;
  }
  :slotted(.filters__search-input),
  :slotted(.filters__select),
  :slotted(.filters__date) {
    height: 40px;
    font-size: 13px;
  }
  :slotted(.filters__range) {
    flex-wrap: wrap;
    gap: 8px;
  }
  :slotted(.filters__date) {
    flex: 1 1 calc(50% - 12px);
    min-width: 0;
  }
  :slotted(.filters__range-sep) {
    flex: 0 0 100%;
    text-align: center;
    order: 3;
    display: none; /* separator reads oddly once dates wrap to two rows */
  }
  .filters__toggles {
    gap: 12px;
    padding-bottom: 6px;
  }
  .filters__btn {
    height: 40px;
    font-size: 13px;
    padding: 0 14px;
  }
}
</style>